export interface Vacancy {
  slug: string;
  title: string;
  hours: string;
  schedule: string;
  salary: string;
  /** Short lead shown on the overview. */
  lead: string;
  sections: { title: string; paragraphs?: string[]; items?: string[] }[];
  apply: { text: string; contact: string; email: string };
}

const DEFAULT_CONTACT = { contact: 'K. Martens', email: 'vacature@excellentcareclinics.nl' };
const ABOUT_ECC =
  'Excellent Care Clinics is een kleinschalige, gespecialiseerde pijnkliniek in Velsen-Noord, waar we patiënten met acute en chronische pijnklachten persoonlijke en hoogwaardige zorg bieden. In onze professionele werkomgeving werken collega’s met korte lijnen nauw samen en is ieders bijdrage zichtbaar.';

/** Full text of the open positions on the current Vacature pages. */
export const vacancies: Vacancy[] = [
  {
    slug: 'anios-basisarts',
    title: 'ANIOS / Basisarts',
    hours: '24 – 32 uur per week',
    schedule: 'Dagdienst, maandag t/m vrijdag',
    salary: 'Cao Ziekenhuizen',
    lead: 'Een unieke combinatie van klinisch werk en academische ontwikkeling bij Excellent Care Clinics International (ECC-I).',
    sections: [
      {
        title: 'Over Excellent Care Clinics International (ECC-I)',
        paragraphs: [
          'Excellent Care Clinics International (ECC-I) is een innovatief behandelcentrum gespecialiseerd in de behandeling van neuropathische pijn, waaronder pijnklachten geassocieerd met post-COVID syndroom. ECC combineert hoogwaardige patiëntenzorg met innovatief klinisch en basaal wetenschappelijk onderzoek gericht op het ontwikkelen van nieuwe behandelstrategieën.',
          'Binnen ECC-I wordt intensief samengewerkt met de Vrije Universiteit Amsterdam en Amsterdam UMC op het gebied van klinisch en basaal wetenschappelijk onderzoek. ECC-I behoort tot de weinige centra in Nederland waar innovatieve patiëntenzorg hand in hand gaat met wetenschappelijk onderzoek naar neuro-inflammatoire en chronische pijnsyndromen.',
        ],
      },
      {
        title: 'De functie',
        paragraphs: [
          'Ben jij een ambitieuze basisarts met interesse in innovatieve patiëntenzorg én wetenschappelijk onderzoek? Dan biedt ECC een unieke combinatie van klinisch werk en academische ontwikkeling. Als ANIOS/basisarts:',
        ],
        items: [
          'voer je zelfstandig spreekuur onder supervisie van ervaren medisch specialisten, waaronder anesthesioloog-pijnspecialisten en een neuroloog;',
          'behandel je patiënten met neuropathische pijnklachten;',
          'werk je mee aan innovatieve behandeltrajecten;',
          'neem je actief deel aan klinisch en basaal wetenschappelijk onderzoek;',
          'draag je bij aan biomarkeronderzoek en translational research op het gebied van neuro-inflammatie en immuunmodulatie;',
          'krijg je de mogelijkheid om onderzoeksprojecten verder uit te bouwen richting publicaties en een promotietraject.',
        ],
      },
      {
        title: 'Wie zoeken wij?',
        paragraphs: ['Wij zoeken een enthousiaste en leergierige basisarts die:'],
        items: [
          'de opleiding Geneeskunde succesvol heeft afgerond;',
          'interesse heeft in complexe chronische aandoeningen, neuro-inflammatie en wetenschappelijk onderzoek;',
          'affiniteit heeft met innovatieve patiëntenzorg;',
          'goed kan samenwerken binnen een multidisciplinair team;',
          'zelfstandig, nauwkeurig en proactief werkt;',
          'ambitie heeft om klinische zorg te combineren met onderzoek en mogelijk promotiewerk.',
        ],
      },
      {
        title: 'Wat bieden wij?',
        items: [
          'Een uitdagende en afwisselende functie binnen een innovatieve klinische en wetenschappelijke werkomgeving',
          'Intensieve begeleiding door ervaren artsen en onderzoekers',
          'Mogelijkheid tot deelname aan wetenschappelijke publicaties en congressen',
          'Mogelijkheid tot promoveren in samenwerking met de Vrije Universiteit Amsterdam',
          'Uitstekende ontwikkelingsmogelijkheden binnen zowel de klinische als academische praktijk',
          'Salaris conform cao Ziekenhuizen, afhankelijk van ervaring',
          '13e maand, vakantiegeld, reiskostenvergoeding en pensioenregeling conform cao',
        ],
      },
    ],
    apply: {
      text: 'Ben jij de basisarts die ons team komt versterken? Stuur dan je cv en korte motivatie. Bij indiensttreding vragen wij een VOG (Verklaring Omtrent Gedrag).',
      contact: 'Miriam de Ruyter, manager',
      email: 'miriam.de.ruyter@excellentcareclinics.nl',
    },
  },
  {
    slug: 'pijnconsulent',
    title: 'Pijnconsulent',
    hours: '24 – 36 uur per week',
    schedule: 'Dagdienst',
    salary: 'FWG 55 · € 3.903 – € 5.503 bruto p/m (fulltime)',
    lead: 'Je begeleidt patiënten met acute en chronische pijnklachten gedurende hun volledige behandeltraject.',
    sections: [
      {
        title: 'Jouw expertise maakt het verschil',
        paragraphs: [
          'Pijn heeft invloed op vrijwel ieder aspect van het dagelijks leven. Als Pijnconsulent bij Excellent Care Clinics begeleid je patiënten met acute en chronische pijnklachten gedurende hun behandeltraject. Je combineert verpleegkundige deskundigheid met voorlichting, begeleiding en zorgcoördinatie en draagt direct bij aan het verbeteren van de kwaliteit van leven van onze patiënten.',
        ],
      },
      {
        title: 'Over de functie',
        paragraphs: [
          'Je monitort het effect van behandelingen, verzorgt follow-up en bent een belangrijk aanspreekpunt voor patiënten, naasten en betrokken zorgverleners. Daarnaast coördineer je zorg rondom patiënten met complexe pijnproblematiek en draag je bij aan de verdere ontwikkeling van zorgprocessen, protocollen en kwaliteit van zorg.',
        ],
      },
      {
        title: 'Wat ga je doen?',
        items: [
          'Begeleiden en informeren van patiënten en hun naasten gedurende het volledige behandeltraject.',
          'Uitvoeren van telefonische en digitale consulten, follow-up gesprekken en nazorgcontacten.',
          'Monitoren van pijnklachten, behandelresultaten, functioneren en medicatiegebruik.',
          'Coördineren en afstemmen van zorg met pijnspecialisten, huisartsen, apotheken, thuiszorg en andere betrokken zorgverleners.',
          'Verrichten van verpleegkundige en medisch ondersteunende handelingen binnen de grenzen van bevoegdheid en bekwaamheid.',
          'Bijdragen aan de ontwikkeling van zorgpaden, protocollen, kwaliteitsverbeteringen en deskundigheidsbevordering.',
        ],
      },
      {
        title: 'Wie ben jij?',
        items: [
          'Je beschikt over een afgeronde opleiding HBO-Verpleegkunde, of over een diploma mbo-verpleegkunde niveau 4 aangevuld met aantoonbaar hbo-werk- en denkniveau en relevante werkervaring in de pijnzorg.',
          'Je bent in het bezit van het diploma Pijnconsulent (HAN) of bereid deze te halen.',
          'Je bent BIG-geregistreerd verpleegkundige.',
          'Je hebt enkele jaren relevante werkervaring als verpleegkundige.',
          'Je hebt bij voorkeur ervaring binnen pijnzorg, anesthesiologie, neurologie, revalidatiezorg of medisch specialistische zorg.',
          'Je hebt kennis van acute en chronische pijnproblematiek, farmacologie en pijnbehandelingen.',
          'Je beschikt over sterke communicatieve vaardigheden en werkt patiëntgericht.',
        ],
      },
      {
        title: 'Wat bieden wij jou?',
        items: [
          'Een afwisselende functie voor 24 – 36 uur per week, waarin je direct bijdraagt aan persoonlijke en hoogwaardige pijnzorg.',
          'Een kleinschalige, professionele werkomgeving met korte lijnen, betrokken collega’s en ruimte om mee te denken.',
          'De mogelijkheid om je verder te ontwikkelen binnen de pijnzorg.',
          'Een arbeidsovereenkomst voor bepaalde tijd van 7 maanden, met uitzicht op een vast dienstverband.',
          'Een salaris volgens FWG 55 van de cao Ziekenhuizen van € 3.903 tot € 5.503 bruto per maand op fulltimebasis (36 uur).',
          'Een pensioenregeling, 8,33% vakantietoeslag en een eindejaarsuitkering van 8,33% volgens de cao Ziekenhuizen.',
          'Dagelijks een aangeboden lunch en een reiskostenvergoeding van € 0,25 per kilometer volgens de geldende ECC-regeling.',
        ],
      },
    ],
    apply: {
      text: 'Wil jij bijdragen aan hoogwaardige pijnzorg binnen een gespecialiseerde kliniek waar aandacht, deskundigheid en betrokkenheid centraal staan? Dan nodigen wij je van harte uit om te solliciteren.',
      ...DEFAULT_CONTACT,
    },
  },
  {
    slug: 'verpleegkundige-niveau-4',
    title: 'Verpleegkundige niveau 4',
    hours: '24 – 36 uur per week',
    schedule: 'Dagdienst, vrij op feestdagen',
    salary: 'FWG 45 · € 3.228 – € 4.346 bruto p/m (36 uur)',
    lead: 'Je biedt patiënten veilige, deskundige en persoonlijke zorg rondom onderzoek, behandeling en herstel.',
    sections: [
      { title: 'Over Excellent Care Clinics', paragraphs: [ABOUT_ECC] },
      {
        title: 'Over de functie',
        paragraphs: [
          'Als Verpleegkundige niveau 4 ben je verantwoordelijk voor het beoordelen, plannen, uitvoeren, coördineren en evalueren van verpleegkundige zorg binnen onze pijnkliniek. Je werkt zelfstandig binnen je bevoegdheid en bekwaamheid en stemt de zorg zorgvuldig af met pijnspecialisten, verpleegkundig specialisten, Verzorgenden IG, medisch secretaresses en externe zorgverleners.',
          'Je valt onder de Verpleegkundig manager en begeleidt waar nodig Verzorgenden IG, stagiaires, leerlingen en nieuwe medewerkers.',
        ],
      },
      {
        title: 'Wat ga je doen?',
        items: [
          'Je stelt verpleegkundige zorgplannen op, voert deze uit en evalueert de resultaten.',
          'Je coördineert de dagelijkse zorg rondom toegewezen patiënten en zorgt voor een volledige en tijdige overdracht.',
          'Je voert verpleegtechnische handelingen, medicatietoediening, controles en monitoring uit volgens protocollen.',
          'Je observeert en beoordeelt de lichamelijke en psychosociale toestand van patiënten en onderneemt tijdig actie.',
          'Je registreert zorg, observaties en bijzonderheden zorgvuldig in het elektronisch patiëntendossier.',
          'Je werkt nauw samen met patiënten, naasten, behandelaren en andere zorgprofessionals.',
          'Je meldt incidenten volgens de geldende procedures en draagt bij aan audits, verbetertrajecten en patiëntveiligheid.',
          'Je begeleidt stagiaires, leerlingen en nieuwe medewerkers en deelt je kennis binnen het team.',
        ],
      },
      {
        title: 'Wie ben jij?',
        items: [
          'Een afgeronde opleiding MBO-Verpleegkunde niveau 4 en een geldige BIG-registratie.',
          'Relevante werkervaring als verpleegkundige; ervaring binnen een klinische of poliklinische omgeving is een pré.',
          'Kennis van verpleegkundige methodieken, klinisch redeneren, verpleegtechnische handelingen en medicatieveiligheid.',
          'Een patiëntgerichte en samenwerkingsgerichte werkwijze, waarbij je helder en professioneel communiceert.',
          'Vaardigheid in het werken met een elektronisch patiëntendossier volgens de Wet BIG, AVG en kwaliteitsrichtlijnen.',
          'Een geldige VOG bij indiensttreding; ervaring met pijnzorg, dagbehandeling of medisch specialistische zorg is een pré.',
        ],
      },
      {
        title: 'Wat bieden wij jou?',
        items: [
          'Werken in dagdienst én vrij op feestdagen.',
          'Een betekenisvolle functie van 24 tot 36 uur per week binnen een gespecialiseerde pijnkliniek.',
          'Een arbeidsovereenkomst voor bepaalde tijd, met de mogelijkheid tot verlenging.',
          'Een salaris in FWG 45 van € 3.228 tot € 4.346 bruto per maand op basis van 36 uur per week.',
          'Pensioenregeling, 8,33% vakantietoeslag en 8,33% eindejaarsuitkering volgens de cao Ziekenhuizen.',
          'Dagelijks een aangeboden lunch en een reiskostenvergoeding van € 0,25 per kilometer.',
          'Ruimte om je te ontwikkelen, bijvoorbeeld richting een eigen verpleegkundig spreekuur of een opleiding tot pijnconsulent.',
        ],
      },
    ],
    apply: {
      text: 'Wil jij als Verpleegkundige niveau 4 bijdragen aan veilige, persoonlijke en professionele pijnzorg? Stuur dan je cv en korte motivatie. Kom je bij ons in dienst, dan vragen we een VOG.',
      ...DEFAULT_CONTACT,
    },
  },
  {
    slug: 'verzorgende-ig',
    title: 'Verzorgende IG',
    hours: '24 – 36 uur per week',
    schedule: 'Dagdienst, vrij op feestdagen',
    salary: 'FWG 40 · € 2.750 – € 3.959 bruto p/m (36 uur)',
    lead: 'Je begeleidt patiënten rondom planbare pijnbehandelingen en zorgt voor een veilig en soepel verloop van iedere behandeldag.',
    sections: [
      { title: 'Over Excellent Care Clinics', paragraphs: [ABOUT_ECC] },
      {
        title: 'Over de functie',
        paragraphs: [
          'Als Verzorgende IG werk je in een specialistische, poliklinische behandelomgeving. Je ontvangt en begeleidt patiënten vóór, tijdens en na hun pijnbehandeling, verricht verpleegtechnische handelingen binnen je bevoegdheid en bekwaamheid en ondersteunt bij een efficiënte doorstroom van het behandelprogramma.',
          'Je valt onder de Verpleegkundig manager en werkt nauw samen met verpleegkundigen, verpleegkundig specialisten, pijnspecialisten, medisch specialisten en medisch secretaresses.',
        ],
      },
      {
        title: 'Wat ga je doen?',
        items: [
          'Je ontvangt patiënten, controleert de benodigde gegevens en bereidt hen voor op de geplande pijnbehandeling.',
          'Je maakt behandelruimten en materialen gereed.',
          'Je voert verpleegtechnische handelingen uit binnen je bevoegdheid en bekwaamheid en ondersteunt de behandelaar.',
          'Je controleert en verstrekt medicatie volgens voorschrift.',
          'Je observeert patiënten vóór, tijdens en na de behandeling en meldt afwijkingen direct.',
          'Je geeft patiënten duidelijke uitleg over het verloop van hun bezoek.',
          'Je legt handelingen, controles en bijzonderheden vast in het elektronisch patiëntendossier.',
          'Je werkt volgens de richtlijnen voor hygiëne, infectiepreventie en patiëntveiligheid.',
        ],
      },
      {
        title: 'Wie ben jij?',
        items: [
          'Een afgeronde opleiding Verzorgende IG niveau 3.',
          'Bij voorkeur ervaring in een ziekenhuis, zelfstandige behandelkliniek of andere klinische of poliklinische omgeving.',
          'Kennis van verpleegtechnische handelingen, medicatieveiligheid, patiëntobservatie en veiligheidsprotocollen.',
          'Een alerte en nauwkeurige werkwijze; je communiceert rustig en duidelijk.',
          'Het vermogen om zelfstandig te werken, snel te schakelen en goed af te stemmen met collega’s.',
          'Een geldige VOG bij indiensttreding en de bereidheid verplichte scholingen te volgen.',
        ],
      },
      {
        title: 'Wat bieden wij jou?',
        items: [
          'Werken in dagdienst én vrij op feestdagen.',
          'Een betekenisvolle functie van 24 tot 36 uur per week binnen een gespecialiseerde pijnkliniek.',
          'Een arbeidsovereenkomst voor bepaalde tijd, met de mogelijkheid tot verlenging.',
          'Een salaris in FWG 40 van € 2.750 tot € 3.959 bruto per maand op basis van 36 uur per week.',
          'Pensioenregeling, 8,33% vakantietoeslag en 8,33% eindejaarsuitkering volgens de cao Ziekenhuizen.',
          'Dagelijks een aangeboden lunch en een reiskostenvergoeding van € 0,25 per kilometer.',
          'Ruimte om je vakkennis en verpleegtechnische vaardigheden verder te ontwikkelen.',
        ],
      },
    ],
    apply: {
      text: 'Wil jij als Verzorgende IG bijdragen aan veilige, persoonlijke en professionele pijnzorg binnen een kleinschalige kliniek? Stuur dan je cv en korte motivatie.',
      ...DEFAULT_CONTACT,
    },
  },
  {
    slug: 'medisch-administratief-medewerker',
    title: 'Medisch administratief medewerker',
    hours: '24 – 32 uur per week',
    schedule: 'Dagdienst',
    salary: 'FWG 35 · € 2.630 – € 3.724 bruto p/m (fulltime)',
    lead: 'Jij zorgt dat verwijzingen, patiëntgegevens, afspraken en correspondentie zorgvuldig en tijdig worden verwerkt.',
    sections: [
      {
        title: 'Jouw nauwkeurigheid houdt onze zorg in beweging',
        paragraphs: [
          'Achter iedere goed georganiseerde behandeling staat een patiëntdossier dat klopt. Dankzij jouw overzicht beschikken collega’s en behandelaars over de juiste informatie en kan het zorgproces soepel doorgaan.',
        ],
      },
      {
        title: 'Over de functie',
        paragraphs: [
          'Je ondersteunt vanuit de backoffice het administratieve proces rondom onze patiëntenzorg. Je werkt zelfstandig binnen de geldende procedures en werkt nauw samen met medisch secretaresses, front- en backoffice en behandelaars. Je valt onder het Afdelingshoofd Backoffice.',
        ],
      },
      {
        title: 'Wat ga je doen?',
        items: [
          'Verwerken van verwijzingen, patiëntgegevens en medische documenten.',
          'Controleren van gegevens en documenten op volledigheid, juistheid en leesbaarheid.',
          'Registreren, ordenen en archiveren van medische en administratieve documenten.',
          'Voorbereiden van dossiers en administratieve werkvoorraden.',
          'Plannen en wijzigen van afspraken binnen de geldende afspraken.',
          'Verwerken van correspondentie met patiënten, verwijzers en externe zorgpartijen.',
          'Opvragen van ontbrekende informatie en bewaken van vervolgacties in het patiëntsysteem.',
        ],
      },
      {
        title: 'Wie ben jij?',
        items: [
          'Je beschikt over mbo werk- en denkniveau, bij voorkeur in een (medisch-)administratieve richting.',
          'Je hebt ervaring met administratieve werkzaamheden; ervaring in een zorgomgeving is een pré.',
          'Je werkt nauwkeurig en gestructureerd en houdt zelfstandig overzicht.',
          'Je communiceert helder en werkt prettig samen.',
          'Je werkt vaardig met digitale patiëntsystemen, e-mail, telefonie en Microsoft Office.',
          'Je gaat zorgvuldig om met vertrouwelijke gegevens en kunt een geldige VOG overleggen.',
        ],
      },
      {
        title: 'Wat bieden wij jou?',
        items: [
          'Een afwisselende medisch-administratieve functie van 24 – 32 uur per week.',
          'Een kleinschalige, professionele werkomgeving met korte lijnen.',
          'Ontwikkelmogelijkheden in medische terminologie, patiëntadministratie en dossiervoering.',
          'Een arbeidsovereenkomst voor bepaalde tijd van 7 maanden, met uitzicht op een vast dienstverband.',
          'Een salaris volgens FWG 35 van € 2.630 tot € 3.724 bruto per maand op fulltimebasis (36 uur).',
          'Pensioenregeling, 8,33% vakantietoeslag en 8,33% eindejaarsuitkering volgens de cao Ziekenhuizen.',
          'Dagelijks een aangeboden lunch en een reiskostenvergoeding van € 0,25 per kilometer.',
        ],
      },
    ],
    apply: {
      text: 'Krijg jij energie van een administratie die klopt en wil je bijdragen aan goede patiëntenzorg? Stuur dan je cv en korte motivatie.',
      ...DEFAULT_CONTACT,
    },
  },
];
